import {Spinner, IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef} from 'react'
import {createPortal} from 'react-dom'
import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {XIcon} from '@primer/octicons-react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useDockedPanel} from '../../contexts/DockedPanelContext'
import {ssrSafeDocument} from '@github-ui/ssr-utils'

type OverviewSidePanelProps = {
  bodyHtml: SafeHTMLString
  isLoading: boolean
  isOpen: boolean
  onClose: () => void
  toggleSidesheetRef: React.RefObject<HTMLElement | null>
}

/**
 * Renders the body content for the Overview Side Panel.
 */
function OverviewPanelBody({bodyHtml, isLoading}: Pick<OverviewSidePanelProps, 'bodyHtml' | 'isLoading'>) {
  // Check if this is the empty description state by looking for the "No description provided." text
  const isEmptyDescription = bodyHtml.includes('No description provided.')

  return isLoading ? (
    <div className="d-flex flex-justify-center">
      <Spinner />
    </div>
  ) : (
    <NewMarkdownViewer verifiedHTML={bodyHtml} className={isEmptyDescription ? 'fgColor-muted' : undefined} />
  )
}

/**
 * Renders the overview side panel for pull requests.
 *
 * Behind the `prs_docked_side_panel` feature flag, content is portaled into
 * the docked sidebar's body slot. Otherwise, a Dialog sidesheet is used.
 */
export function OverviewSidePanel({bodyHtml, isLoading, isOpen, onClose, toggleSidesheetRef}: OverviewSidePanelProps) {
  const isPrsDockedSidePanelEnabled = isFeatureEnabled('prs_docked_side_panel')
  const panelName = useDockedPanel()
  const overviewDockedSidePanelIsOpen = panelName === 'overview'
  // Get portal target — only accessed when panel is open, so SSR-safe
  const sidePanelBody = overviewDockedSidePanelIsOpen ? ssrSafeDocument?.getElementById('docked-side-panel-body') : null

  const closeButtonRef = useRef<HTMLButtonElement>(null)

  if (!isOpen) return null

  if (isPrsDockedSidePanelEnabled && sidePanelBody) {
    return createPortal(<OverviewPanelBody bodyHtml={bodyHtml} isLoading={isLoading} />, sidePanelBody)
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={onClose}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Overview</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close overview panel"
              icon={XIcon}
              variant="invisible"
              onClick={onClose}
            />
          </div>
        </Dialog.Header>
      )}
    >
      <OverviewPanelBody bodyHtml={bodyHtml} isLoading={isLoading} />
    </Dialog>
  )
}
